import { Button } from 'components/Button';
import { Icon } from 'components/Icon';
import { useAppContext } from 'hooks';
import { useState, useRef, useEffect } from 'react';
import styles from './ShaderToggle.module.css';

const shaderOptions = [
  { id: 'meshGradient', label: 'Metallic' },
  { id: 'northernLights', label: 'Aurora' },
  { id: 'grainGradient', label: 'Grain' },
  { id: 'godRays', label: 'Rays' },
  // Hidden for now - not production ready:
  // { id: 'displacementSphere', label: 'Spikes' },
  // { id: 'liquidMetal', label: 'Liquid' },
  // { id: 'neuroNoise', label: 'Neuro' },
];

export const ShaderToggle = ({ isMobile, ...rest }) => {
  const { shader, shaderCropped, dispatch } = useAppContext();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleSelect = (shaderId) => {
    dispatch({ type: 'setShader', value: shaderId });
    window.localStorage.setItem('shader', JSON.stringify(shaderId));
    setIsOpen(false);
  };

  const handleFullPageToggle = () => {
    const newValue = !shaderCropped;
    dispatch({ type: 'setShaderCropped', value: newValue });
    window.localStorage.setItem('shaderCropped', JSON.stringify(newValue));
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  return (
    <div className={styles.container} data-mobile={isMobile} ref={dropdownRef}>
      <Button
        iconOnly
        className={styles.toggle}
        aria-label="Change shader"
        aria-expanded={isOpen}
        onClick={handleToggle}
        {...rest}
      >
        <Icon className={styles.icon} icon="palette" />
      </Button>

      {isOpen && (
        <div className={styles.dropdown} role="listbox" aria-label="Select shader">
          {shaderOptions.map((option) => (
            <button
              key={option.id}
              className={styles.option}
              data-selected={shader === option.id}
              role="option"
              aria-selected={shader === option.id}
              onClick={() => handleSelect(option.id)}
            >
              {shader === option.id && (
                <Icon className={styles.check} icon="check" />
              )}
              <span>{option.label}</span>
            </button>
          ))}
          <div className={styles.divider} />
          <label className={styles.checkboxLabel} htmlFor="fullpage-shader-toggle">
            <input
              id="fullpage-shader-toggle"
              type="checkbox"
              className={styles.checkbox}
              checked={!shaderCropped}
              onChange={handleFullPageToggle}
              aria-label="Enable full page shader background"
            />
            <span>Full page</span>
          </label>
        </div>
      )}
    </div>
  );
};
